// ── Invites tab — per-session invite log reads (Wave 3, REAL read) ──────────
// Reads cohort sessions, then the selected session's invite log via
// useHonestQuery -> CohortTabShell. Session picker is a collection filter.

import { CollectionPagination, CollectionToolbar, DataTableV2 } from "@vjal/ui";
import {  useMemo, useState } from "react";

import { useHonestQuery } from "../../../components/honest-state";
import { getCoachingServices, getCoachingZ6Services } from "../../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../../hooks/coaching-query-keys";
import type { CoachingSessionDto, ListResponse } from "../../../services/coaching-dtos";
import type { CoachingInviteLogDto } from "../../../services/coaching-z6-dtos";
import { CohortTabShell } from "./cohort-tab-shell";
import { mapInviteEntries, useCohortInvitesCollection } from "./cohort-invites-collection";

const services = getCoachingServices();

export function CohortInvitesTab({ cohortId }: { cohortId: string }) {
  const sessionsQuery = useHonestQuery<ListResponse<CoachingSessionDto>>(
    coachingQueryKeys.list("sessions.listForCohort", { cohort_id: cohortId }),
    () => services.sessions.listForCohort(cohortId)
  );
  const sessions = sessionsQuery.data?.items ?? [];
  const defaultSessionId = sessions[0]?.id ?? "";
  const [activeSessionId, setActiveSessionId] = useState("");

  // Render adjustments, not effects: synchronous setState in effects cascades
  // renders (bug-pack class, 2026-07-06). Guarded => converges in one pass.
  if (defaultSessionId && !activeSessionId) setActiveSessionId(defaultSessionId);

  const inviteQuery = useHonestQuery<CoachingInviteLogDto>(
    coachingQueryKeys.z6.inviteLog(activeSessionId || "missing"),
    () => getCoachingZ6Services().inviteLog.forSession(activeSessionId),
    {
      enabled: Boolean(activeSessionId),
      disabledPosture: "empty",
      isEmpty: (data) => (data.inviteLog?.entries ?? []).length === 0
    }
  );

  const allRows = useMemo(
    () => mapInviteEntries(inviteQuery.data?.inviteLog.entries ?? [], activeSessionId),
    [inviteQuery.data?.inviteLog.entries, activeSessionId]
  );
  const collection = useCohortInvitesCollection(allRows, sessions, activeSessionId || defaultSessionId);

  const filterSessionId = collection.state.filters.session_id;
  const [prevFilterSessionId, setPrevFilterSessionId] = useState(filterSessionId);
  if (filterSessionId !== prevFilterSessionId) {
    setPrevFilterSessionId(filterSessionId);
    if (typeof filterSessionId === "string" && filterSessionId && filterSessionId !== activeSessionId) {
      setActiveSessionId(filterSessionId);
    }
  }

  if (sessionsQuery.posture !== null || sessions.length === 0) {
    return (
      <CohortTabShell
        title="Invites"
        subtitle="Per-session invite trigger log (append-only; no provider payloads, emails, or passcodes)."
        posture={sessionsQuery.posture ?? "empty"}
        gate={sessionsQuery.gate}
        reference={sessionsQuery.reference}
        onRetry={sessionsQuery.retry}
        emptyTitle="No sessions yet"
        emptyMessage="The invite log appears per session once sessions exist and invites are triggered."
      >
        <span />
      </CohortTabShell>
    );
  }

  return (
    <CohortTabShell
      title="Invites"
      subtitle="Per-session invite trigger log (append-only; no provider payloads, emails, join URLs, or passcodes)."
      posture={inviteQuery.posture}
      gate={inviteQuery.gate}
      reference={inviteQuery.reference}
      onRetry={inviteQuery.retry}
      emptyTitle="No invite attempts yet"
      emptyMessage="Invite attempts for the selected session will appear here once invites are triggered."
    >
      <CollectionToolbar collection={collection} className="rounded-xl border border-line" stableChipNames />
      <div className="rounded-xl border border-line bg-white">
        <DataTableV2 collection={collection} caption="Session invite log" />
        {collection.uiState === "ready" && <CollectionPagination collection={collection} className="px-4" />}
      </div>
    </CohortTabShell>
  );
}